import { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import { Switch, Route, useLocation, Redirect } from 'wouter';
import { EntryScreen } from './components/screens/EntryScreen';
import { UnifiedPlayerScreen } from './components/screens/UnifiedPlayerScreen';
import { PrivacyScreen } from './components/screens/PrivacyScreen';
import { ConnectToSaveModal } from './components/ui/ConnectToSaveModal';
import { ThemeToggle } from './components/ui/ThemeToggle';
import { SettingsDropdown } from './components/ui/SettingsDropdown';
import { AffiliateDisplayToggle } from './components/ui/AffiliateDisplayToggle';
import { YouTubeVideoThumbnail } from './components/ui/YouTubeVideoThumbnail';
import { Footer } from './components/ui/Footer';
import { Toast } from './components/ui/Toast';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { VisualEffectsProvider, useVisualEffects } from './contexts/VisualEffectsContext';
import { AffiliateDisplayProvider } from './contexts/AffiliateDisplayContext';
import { useActivePlayer } from './hooks/useActivePlayer';
import { useLocalStorage } from './hooks/useLocalStorage';
import { buildAppleMusicLinks } from './lib/apple-music-links';
import { generatePlaylist } from './lib/playlist-generator';
import { loadPlaylistHistory, recordGeneratedPlaylist } from './lib/playlist-history';
import { DEV_MODE } from './lib/dev-stubs';
import {
  trackSessionStarted,
  trackPlaylistGenerated,
  trackPlaybackStarted,
  trackPlaybackPaused,
  trackTrackSkipped,
  trackPlaylistSaved,
  trackPlaylistSaveFailed,
  trackPlaylistRegenerated,
  trackAppleMusicLinked,
} from './lib/analytics';
import type { AppScreen, TreatmentType, GeneratedPlaylist, ResolvedTrack, PhaseOverrides, Platform } from './types';

const ParticleBackground = lazy(() =>
  import('./components/ui/ParticleBackground').then((module) => ({
    default: module.ParticleBackground,
  }))
);

const YOUTUBE_PLAYER_ID = 'youtube-player';

function AppContent() {
  const { resolvedTheme } = useTheme();
  const { settings: visualEffects } = useVisualEffects();
  const [location, navigate] = useLocation();
  const pathname = location.split('?')[0].split('#')[0] || '/';
  const screen: AppScreen = pathname === '/player' ? 'player' : 'entry';
  const [currentPlaylist, setCurrentPlaylist] = useState<GeneratedPlaylist | null>(null);
  const [resolvedTracks, setResolvedTracks] = useState<ResolvedTrack[]>([]);
  const [savedTreatmentType, setSavedTreatmentType] = useLocalStorage<TreatmentType>('healfm-treatment-type', 'im');
  const [selectedPlatform, setSelectedPlatform] = useLocalStorage<Platform>('healfm-platform', 'none');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [lastDurationMinutes, setLastDurationMinutes] = useState<number>(60);
  const [hasSavedCurrentPlaylist, setHasSavedCurrentPlaylist] = useState(false);
  const [savedPlaylistId, setSavedPlaylistId] = useState<string | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);

  const activePlayer = useActivePlayer({
    platform: selectedPlatform,
    youtubeContainerId: YOUTUBE_PLAYER_ID,
    isOnPlayerScreen: screen === 'player',
  });

  const { musicKit } = activePlayer;
  const { isConfigured, isLoading, configure } = musicKit;

  // Configure MusicKit on mount
  const [configAttempted, setConfigAttempted] = useState(false);

  useEffect(() => {
    if (!isConfigured && !isLoading && !configAttempted) {
      // In dev mode, configure immediately without waiting for MusicKit script
      if (DEV_MODE) {
        setConfigAttempted(true);
        configure();
        return;
      }
      // Check if MusicKit script is loaded first
      let timeoutId: ReturnType<typeof setTimeout>;
      let attempts = 0;
      const MAX_ATTEMPTS = 100; // 10 seconds at 100ms intervals

      const checkAndConfigure = () => {
        if (window.MusicKit) {
          setConfigAttempted(true);
          configure();
        } else if (attempts < MAX_ATTEMPTS) {
          attempts++;
          timeoutId = setTimeout(checkAndConfigure, 100);
        } else {
          setConfigAttempted(true);
          setLocalError('Unable to load Apple Music. Please check your connection or ad blocker.');
        }
      };
      checkAndConfigure();

      return () => clearTimeout(timeoutId);
    }
  }, [isConfigured, isLoading, configAttempted, configure]);

  // Handle platform selection on entry screen
  const handleSelectPlatform = useCallback((platform: Platform) => {
    setLocalError(null);
    setSelectedPlatform(platform);
  }, [setSelectedPlatform]);

  // Reset session state shared across multiple handlers
  const resetSessionState = useCallback((opts?: { clearPlatform?: boolean }) => {
    setCurrentPlaylist(null);
    setResolvedTracks([]);
    setLocalError(null);
    setSaveSuccess(false);
    setSaveError(null);
    setSavedPlaylistId(null);
    if (opts?.clearPlatform) {
      setSelectedPlatform('none');
    }
  }, [setSelectedPlatform]);

  // Handle playlist generation - go directly to player with skeleton, then resolve
  const handleGenerate = useCallback(
    async (treatmentType: TreatmentType, durationMinutes: number, phaseOverrides?: PhaseOverrides) => {
      setLocalError(null);
      setSavedTreatmentType(treatmentType);
      setLastDurationMinutes(durationMinutes);
      setHasSavedCurrentPlaylist(false);
      setSavedPlaylistId(null);
      try {
        // Load history for anti-repetition weighting
        const history = loadPlaylistHistory();

        // Generate the playlist locally with history-aware selection and optional phase overrides
        const playlist = await generatePlaylist(treatmentType, durationMinutes, history, phaseOverrides);

        // Record this playlist's tracks in history for future sessions
        recordGeneratedPlaylist(playlist);

        setCurrentPlaylist(playlist);
        trackPlaylistGenerated({
          platform: selectedPlatform,
          trackCount: playlist.tracks.length,
          treatmentType,
          durationMinutes,
        });
        setResolvedTracks([]);
        navigate('/player');

        // Start resolution and playback (async, shows skeleton while loading)
        void activePlayer.resolveAndStart(playlist).then(resolved => {
          if (resolved.length > 0) {
            setResolvedTracks(resolved);
          }
        });
      } catch (error) {
        setLocalError(error instanceof Error ? error.message : 'Failed to generate playlist');
      }
    },
    [
      setSavedTreatmentType,
      navigate,
      activePlayer,
      selectedPlatform,
    ]
  );

  // Handle "Begin Session" from entry screen - authorize if needed, then generate
  const handleBeginSession = useCallback(async (
    treatmentType: TreatmentType,
    durationMinutes: number,
    phaseOverrides?: PhaseOverrides,
  ) => {
    setLocalError(null);
    trackSessionStarted({
      treatmentType,
      durationMinutes,
      platform: selectedPlatform,
      hasPhaseOverrides: !!phaseOverrides,
    });

    // For Apple Music, ensure authorization before generating
    if (selectedPlatform === 'apple-music' && !musicKit.isAuthorized) {
      try {
        const authorized = await musicKit.authorize();
        if (!authorized) return;
        trackAppleMusicLinked({ source: 'session_start' });
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : 'Failed to connect to Apple Music');
        return;
      }
    }

    await handleGenerate(treatmentType, durationMinutes, phaseOverrides);
  }, [selectedPlatform, musicKit, handleGenerate]);

  // Handle regenerating playlist with same settings
  const handleRegenerate = useCallback(async () => {
    if (!currentPlaylist) return;
    trackPlaylistRegenerated({
      treatmentType: savedTreatmentType,
      durationMinutes: lastDurationMinutes,
      platform: selectedPlatform,
    });
    activePlayer.pauseActive();
    await handleGenerate(savedTreatmentType, lastDurationMinutes);
  }, [
    currentPlaylist,
    savedTreatmentType,
    lastDurationMinutes,
    handleGenerate,
    activePlayer,
    selectedPlatform,
  ]);

  // Handle retry resolution after error
  const handleRetryResolution = useCallback(() => {
    if (currentPlaylist) {
      activePlayer.resolveAndStart(currentPlaylist).then(resolved => {
        if (resolved.length > 0) {
          setResolvedTracks(resolved);
        }
      });
    }
  }, [currentPlaylist, activePlayer]);

  // Handle saving playlist to Apple Music (shows toast, player keeps playing)
  const handleSaveToLibrary = useCallback(async () => {
    if (!currentPlaylist) return;

    // YouTube playback does not support saving to Apple Music
    if (activePlayer.isYouTubeSelection) {
      return;
    }

    // If in preview mode, show connect modal instead
    if (activePlayer.isPreviewSelection) {
      setShowConnectModal(true);
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const playlistId = await musicKit.createPlaylist(currentPlaylist);

      if (playlistId) {
        setSaveSuccess(true);
        setHasSavedCurrentPlaylist(true);
        setSavedPlaylistId(playlistId);
        trackPlaylistSaved({
          platform: selectedPlatform,
          treatmentType: savedTreatmentType,
          trackCount: currentPlaylist.tracks.length,
          durationMinutes: lastDurationMinutes,
        });
      } else {
        throw new Error(musicKit.error || 'Failed to create playlist');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save playlist';
      trackPlaylistSaveFailed({ platform: selectedPlatform, error: errorMessage });
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [currentPlaylist, musicKit, selectedPlatform, savedTreatmentType, lastDurationMinutes, activePlayer]);

  // Handle connecting from preview mode (allows saving mid-session)
  const handleConnectFromPreview = useCallback(async () => {
    setShowConnectModal(false);
    setLocalError(null);
    setSaveError(null);

    try {
      const authorized = await musicKit.authorize();
      if (!authorized) {
        return;
      }

      trackAppleMusicLinked({ source: 'connect_from_preview' });
      // Pause preview playback
      activePlayer.pauseActive();

      // Re-resolve tracks with full playback capability
      if (currentPlaylist) {
        const isrcs = currentPlaylist.tracks.map(t => t.isrc);
        const resolution = await musicKit.resolveIsrcs(isrcs);

        const resolved: ResolvedTrack[] = currentPlaylist.tracks
          .filter(t => resolution.resolved[t.isrc])
          .map(t => ({
            isrc: t.isrc,
            catalogId: resolution.resolved[t.isrc],
            title: t.title,
            artist: t.artist,
            durationSeconds: t.durationSeconds,
            phase: t.phase,
          }));

        if (resolved.length === 0) {
          throw new Error('No tracks could be resolved for full playback. Still in preview mode.');
        }

        const catalogIds = resolved.map(t => t.catalogId);
        await musicKit.setQueue(catalogIds);
        setResolvedTracks(resolved);
      }

      // Switch to authenticated mode after successful queue prep
      setSelectedPlatform('apple-music');

      if (currentPlaylist) {
        await musicKit.play();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to connect';
      setLocalError(message);
      setSaveError(message);
    }
  }, [musicKit, currentPlaylist, setSelectedPlatform, activePlayer]);

  // Handle creating a new playlist
  const handleNewPlaylist = useCallback(() => {
    activePlayer.pauseActive();
    setCurrentPlaylist(null);
    setResolvedTracks([]);
    setSaveSuccess(false);
    setSaveError(null);
    setSavedPlaylistId(null);
    navigate('/');
  }, [activePlayer, navigate]);

  // Handle logo click - reset to welcome screen
  const handleLogoClick = useCallback(() => {
    activePlayer.pauseAndReset();
    resetSessionState({ clearPlatform: true });
    navigate('/');
  }, [activePlayer, resetSessionState, navigate]);

  // Handle disconnect from Apple Music / exit preview mode
  const handleDisconnect = useCallback(async () => {
    activePlayer.pauseAndReset();
    if (activePlayer.isAppleMusicSelection) {
      await musicKit.unauthorize();
    }
    resetSessionState({ clearPlatform: true });
    navigate('/');
  }, [activePlayer, musicKit, resetSessionState, navigate]);

  // Dismiss save success toast
  const handleDismissSaveSuccess = useCallback(() => {
    setSaveSuccess(false);
  }, []);

  // Dismiss save error toast
  const handleDismissSaveError = useCallback(() => {
    setSaveError(null);
  }, []);

  // Playback wrappers with analytics tracking
  const handlePlay = useCallback(async () => {
    trackPlaybackStarted({
      platform: selectedPlatform,
      treatmentType: savedTreatmentType,
      trackIndex: activePlayer.currentTrackIndex,
    });
    await activePlayer.play();
  }, [selectedPlatform, savedTreatmentType, activePlayer]);

  const handlePause = useCallback(() => {
    trackPlaybackPaused({
      platform: selectedPlatform,
      currentTrackIndex: activePlayer.currentTrackIndex,
    });
    activePlayer.pause();
  }, [selectedPlatform, activePlayer]);

  const handleSkipNext = useCallback(async () => {
    trackTrackSkipped({
      platform: selectedPlatform,
      direction: 'next',
      fromIndex: activePlayer.currentTrackIndex,
      toIndex: Math.min(activePlayer.currentTrackIndex + 1, Math.max(resolvedTracks.length - 1, 0)),
    });
    await activePlayer.skipToNext();
  }, [selectedPlatform, activePlayer, resolvedTracks.length]);

  const handleSkipPrevious = useCallback(async () => {
    trackTrackSkipped({
      platform: selectedPlatform,
      direction: 'previous',
      fromIndex: activePlayer.currentTrackIndex,
      toIndex: Math.max(activePlayer.currentTrackIndex - 1, 0),
    });
    await activePlayer.skipToPrevious();
  }, [selectedPlatform, activePlayer]);

  const handleSkipToTrack = useCallback(async (index: number) => {
    trackTrackSkipped({
      platform: selectedPlatform,
      direction: 'direct',
      fromIndex: activePlayer.currentTrackIndex,
      toIndex: index,
    });
    await activePlayer.skipToTrack(index);
  }, [selectedPlatform, activePlayer]);

  // Update document title with current track on player screen
  useEffect(() => {
    const defaultTitle = 'heal.fm';
    const playlistTitle = currentPlaylist?.name;
    const shouldShowPlaylistTitle =
      screen === 'player' &&
      !!playlistTitle &&
      (activePlayer.playbackState === 'stopped' || activePlayer.isPreviewUi);
    const playbackEmoji =
      activePlayer.playbackState === 'playing' ? '▶️' :
      activePlayer.playbackState === 'paused' ? '⏸️' :
      '';

    if (shouldShowPlaylistTitle) {
      document.title = `${playlistTitle} | heal.fm`;
    } else if (screen === 'player' && resolvedTracks.length > 0) {
      const currentTrack = resolvedTracks[activePlayer.currentTrackIndex];
      if (currentTrack) {
        const emojiPrefix = playbackEmoji ? `${playbackEmoji} ` : '';
        document.title = `${emojiPrefix}${currentTrack.title} – ${currentTrack.artist} | heal.fm`;
      } else {
        document.title = defaultTitle;
      }
    } else {
      document.title = defaultTitle;
    }

    // Cleanup: restore default title when unmounting
    return () => {
      document.title = defaultTitle;
    };
  }, [
    screen,
    resolvedTracks,
    currentPlaylist?.name,
    activePlayer.currentTrackIndex,
    activePlayer.playbackState,
    activePlayer.isPreviewUi,
  ]);

  const appleMusicLinks = savedPlaylistId ? buildAppleMusicLinks(savedPlaylistId) : null;
  const showConnectionStatus = !activePlayer.isYouTubeSelection && (musicKit.isAuthorized || activePlayer.isPreviewUi);
  const connectionLabel = activePlayer.isPreviewUi ? 'Preview Mode' : 'Connected';
  const connectionTitle = activePlayer.isPreviewUi ? 'Click to exit preview mode' : 'Click to disconnect from Apple Music';
  const showYouTubeErrorToast = activePlayer.isYouTubeSelection && !!activePlayer.youtubeError;

  // Combined error state
  const error = localError || musicKit.error;

  return (
    <div className={`min-h-screen flex flex-col relative ${visualEffects.enhancedAnimations ? 'has-micro-interactions' : ''}`}>
      {/* Particle Background */}
      {visualEffects.particles && (
        <Suspense fallback={null}>
          <ParticleBackground resolvedTheme={resolvedTheme} />
        </Suspense>
      )}

      {/* Toast notifications */}
      <Toast
        message="Playlist saved to Apple Music!"
        variant="success"
        isVisible={saveSuccess}
        onDismiss={handleDismissSaveSuccess}
      />
      <Toast
        message={saveError || 'Failed to save playlist'}
        variant="error"
        isVisible={!!saveError}
        onDismiss={handleDismissSaveError}
      />
      <Toast
        key={activePlayer.youtubeError?.id ?? 'youtube-error'}
        message={activePlayer.youtubeError?.message ?? 'YouTube playback error'}
        variant="error"
        isVisible={showYouTubeErrorToast}
        onDismiss={activePlayer.clearYouTubeError}
      />

      {/* Connect to Save Modal (preview mode) */}
      <ConnectToSaveModal
        isOpen={showConnectModal}
        onConnect={handleConnectFromPreview}
        onDismiss={() => setShowConnectModal(false)}
        isLoading={musicKit.isLoading}
      />

      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4">
        <button
          onClick={handleLogoClick}
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <div className="w-8 h-8 rounded-lg overflow-hidden">
            <img
              src="/logo-512.png"
              alt=""
              className="w-full h-full object-cover scale-[1.08]"
              aria-hidden="true"
            />
          </div>
          <span className="font-semibold text-parchment-text dark:text-white/90">heal.fm</span>
        </button>

        <div className="flex items-center gap-3">
          <SettingsDropdown />
          <AffiliateDisplayToggle />
          <ThemeToggle />
          {showConnectionStatus && (
            <button
              onClick={handleDisconnect}
              className="flex items-center gap-2 text-sm text-parchment-400 dark:text-white/50 hover:text-parchment-text/80 dark:hover:text-white/70 transition-colors cursor-pointer"
              title={connectionTitle}
            >
              <div className={`w-2 h-2 rounded-full ${activePlayer.isPreviewUi ? 'bg-amber-400' : 'bg-green-400'} animate-pulse-dot`} />
              {connectionLabel}
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        <Switch>
          <Route path="/">
            <EntryScreen
              selectedPlatform={selectedPlatform}
              onSelectPlatform={handleSelectPlatform}
              onBeginSession={handleBeginSession}
              isLoading={selectedPlatform === 'apple-music' && musicKit.isLoading}
              error={error}
              initialTreatmentType={savedTreatmentType}
              isAppleMusicAuthorized={musicKit.isAuthorized}
            />
          </Route>
          <Route path="/player">
            {!currentPlaylist ? (
              <Redirect to="/" replace />
            ) : (
              <UnifiedPlayerScreen
                playlist={currentPlaylist}
                resolvedTracks={resolvedTracks}
                treatmentType={savedTreatmentType}
                isResolving={activePlayer.isResolving}
                resolutionError={activePlayer.resolutionError}
                playbackState={activePlayer.playbackState}
                isPlaying={activePlayer.isPlaying}
                isPreviewMode={activePlayer.isPreviewUi}
                platform={selectedPlatform}
                currentTrackIndex={activePlayer.currentTrackIndex}
                artworkUrl={activePlayer.currentArtworkUrl}
                onPlay={handlePlay}
                onPause={handlePause}
                onSkipNext={handleSkipNext}
                onSkipPrevious={handleSkipPrevious}
                onSkipToTrack={handleSkipToTrack}
                onSaveToLibrary={handleSaveToLibrary}
                onNewPlaylist={handleNewPlaylist}
                onRegenerate={handleRegenerate}
                onRetryResolution={handleRetryResolution}
                isSaving={isSaving}
                hasSaved={hasSavedCurrentPlaylist}
                appleMusicLinks={appleMusicLinks}
                onConnectFromPreview={activePlayer.isPreviewSelection ? () => setShowConnectModal(true) : undefined}
              />
            )}
          </Route>
          <Route path="/privacy">
            <PrivacyScreen />
          </Route>
          <Route>
            <Redirect to="/" replace />
          </Route>
        </Switch>
      </main>

      {screen === 'player' && selectedPlatform === 'youtube' && (
        <YouTubeVideoThumbnail
          playerId={YOUTUBE_PLAYER_ID}
          playbackState={activePlayer.playbackState}
          isPlaying={activePlayer.isPlaying}
          onPlay={activePlayer.play}
          onPause={activePlayer.pause}
        />
      )}

      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <VisualEffectsProvider>
        <AffiliateDisplayProvider>
          <AppContent />
        </AffiliateDisplayProvider>
      </VisualEffectsProvider>
    </ThemeProvider>
  );
}

export default App;
